import { Listeners } from 'utils';
import { CosPaginatedGrid } from 'common';

class CosCollectionGrid extends CosPaginatedGrid {
  connectedCallback() {
    super.connectedCallback();
    // Per-request AbortController field — reset before each fetch call.
    this.fetchAbortController = null;
  }

  disconnectedCallback() {
    // Abort any in-flight load-more fetch (skill: cos-theme/fetch-abort-on-disconnect).
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
    super.disconnectedCallback();
  }

  /**
   * Override base getPage to apply per-request abort pattern.
   * Aborts the previous request before starting a new one, preventing
   * stale load-more responses from overwriting fresher ones.
   */
  async getPage(url) {
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
    }
    this.fetchAbortController = new AbortController();
    const signal = this.fetchAbortController.signal;

    const sep = url.includes('?') ? '&' : '?';
    try {
      const response = await fetch(`${url}${sep}section_id=${this.settings.sectionId}`, { signal });
      if (!response.ok) throw new Error(`Failed to load page: ${response.status} ${response.statusText}`);
      return response.text();
    } catch (err) {
      if (err.name === 'AbortError') return; // expected on rapid load-more or teardown
      console.error('[cos-collection-grid] fetch failed', err);
      throw err;
    }
  }
}
if (!customElements.get('cos-collection-grid')) customElements.define('cos-collection-grid', CosCollectionGrid);

/**
 * Dual-handle price slider in the collection/search filter sidebar.
 *
 * Visual only here — it does NOT fetch. On release it mirrors the slider values
 * into the named number inputs and bubbles a `cos:price-changed` event up to
 * <cos-collection> (collection-section.js), which owns every filter fetch
 * (single request + pushState + products-count + SEO head sync). This keeps the
 * price filter on the exact same code path as checkboxes/sort/pagination.
 */
class CosPriceRange extends HTMLElement {
  connectedCallback() {
    this.listeners = new Listeners();

    // Markup order (results-grid.liquid): [slider-min, slider-max, number-min, number-max].
    // The two `<input type="range">` sliders carry no `name`; the two number inputs
    // carry the Shopify price filter param names (filter.v.price.gte / .lte).
    const inputs = this.querySelectorAll('input');
    if (inputs.length < 2) return;
    [this.inputStart, this.inputEnd, this.numberStart, this.numberEnd] = inputs;

    // CSS paints the between-track via --range-min/--range-max on .range-group
    // (main.css `.range-group { background: linear-gradient(... var(--range-min) ...) }`).
    this.rangeGroup = this.querySelector('.range-group');

    this.updateFill();
    this.setEvents();
  }

  disconnectedCallback() {
    if (this.listeners) {
      this.listeners.removeAll();
    }
  }

  /** Clamped [lo, hi] of the two slider values (handles can be dragged past each other). */
  bounds() {
    const a = parseInt(this.inputStart.value, 10);
    const b = parseInt(this.inputEnd.value, 10);
    return [Math.min(a, b), Math.max(a, b)];
  }

  /** Repaint the between-track to match the current slider positions. */
  updateFill() {
    if (!this.rangeGroup) return;
    const rangeMax = parseFloat(this.inputStart.max);
    const [lo, hi] = this.bounds();
    const minPct = rangeMax ? (lo / rangeMax) * 100 : 0;
    const maxPct = rangeMax ? (hi / rangeMax) * 100 : 100;
    this.rangeGroup.style.setProperty('--range-min', minPct + '%');
    this.rangeGroup.style.setProperty('--range-max', maxPct + '%');
  }

  /**
   * On release: mirror values into the named number inputs and hand off to the
   * section via cos:price-changed. min/max are sent as null when a handle sits at
   * its floor/ceiling so the section drops the param instead of writing gte=0 / lte=max.
   */
  handleInputChange() {
    const [lo, hi] = this.bounds();
    const floor = parseFloat(this.inputStart.min);
    const ceil = parseFloat(this.inputStart.max);

    if (this.numberStart) this.numberStart.value = lo;
    if (this.numberEnd) this.numberEnd.value = hi;

    this.dispatchEvent(new CustomEvent('cos:price-changed', {
      bubbles: true,
      detail: {
        minName: this.numberStart ? this.numberStart.name : null,
        min: lo > floor ? lo : null,
        maxName: this.numberEnd ? this.numberEnd.name : null,
        max: hi < ceil ? hi : null,
      },
    }));
  }

  setEvents() {
    // Live feedback during drag: repaint fill + mirror into the number boxes.
    // Assigning input.value programmatically emits no change event, so nothing fetches here.
    const onDrag = () => {
      this.updateFill();
      const [lo, hi] = this.bounds();
      if (this.numberStart) this.numberStart.value = lo;
      if (this.numberEnd) this.numberEnd.value = hi;
    };
    this.listeners.add(this.inputStart, 'input', onDrag);
    this.listeners.add(this.inputEnd, 'input', onDrag);

    // change = release. stopPropagation so the nameless slider never reaches the
    // section's `change #desktop-filters-form input` delegate — that would set a junk
    // empty-key param and fire a second, redundant fetch. cos:price-changed drives the
    // single real fetch instead.
    const onRelease = (event) => {
      event.stopPropagation();
      this.handleInputChange();
    };
    this.listeners.add(this.inputStart, 'change', onRelease);
    this.listeners.add(this.inputEnd, 'change', onRelease);
  }
}
if (!customElements.get('cos-price-range')) customElements.define('cos-price-range', CosPriceRange);
